import React from 'react';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, ArrowDownLeft, ArrowUpRight, Building, User, CheckCheck, Check, Clock, XCircle } from 'lucide-react';
import { WhatsAppMessage } from '@/types/whatsapp';
import { formatDistanceToNow } from 'date-fns';

interface WhatsAppMessageCardProps {
  message: WhatsAppMessage;
}

export const WhatsAppMessageCard: React.FC<WhatsAppMessageCardProps> = ({ message }) => {
  const getDirectionBadge = () => {
    if (message.direction === 'INCOMING') {
      return (
        <Badge className="bg-green-100 text-green-800">
          <ArrowDownLeft className="w-3 h-3 mr-1" />
          Incoming
        </Badge>
      );
    }
    return (
      <Badge className="bg-blue-100 text-blue-800">
        <ArrowUpRight className="w-3 h-3 mr-1" />
        Outgoing
      </Badge>
    );
  };

  const getTypeBadge = () => {
    const typeColors: Record<string, string> = {
      MANUAL: 'bg-gray-100 text-gray-800',
      AUTO: 'bg-purple-100 text-purple-800',
      WELCOME: 'bg-blue-100 text-blue-800',
      REMINDER: 'bg-yellow-100 text-yellow-800',
      FOLLOW_UP: 'bg-orange-100 text-orange-800',
      PROPOSAL: 'bg-indigo-100 text-indigo-800',
      NEGOTIATION: 'bg-pink-100 text-pink-800',
    };

    return (
      <Badge className={typeColors[message.type] || 'bg-gray-100 text-gray-800'}>
        {message.type}
      </Badge>
    );
  };

  const getStatusIcon = () => {
    switch (message.status) {
      case 'READ':
        return <CheckCheck className="w-4 h-4 text-blue-500" />;
      case 'DELIVERED':
        return <CheckCheck className="w-4 h-4 text-gray-500" />;
      case 'SENT':
        return <Check className="w-4 h-4 text-gray-500" />;
      case 'FAILED':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          {getDirectionBadge()}
          {getTypeBadge()}
          {message.status && (
            <div className="flex items-center space-x-1">
              {getStatusIcon()}
              <span className="text-xs text-gray-500 capitalize">{message.status.toLowerCase()}</span>
            </div>
          )}
        </div>
        <span className="text-sm text-gray-500">
          {formatDistanceToNow(new Date(message.timestamp), { addSuffix: true })}
        </span>
      </div>

      {/* Phone Number */}
      <div className="mb-2">
        <span className="text-sm font-medium text-gray-700">
          {message.direction === 'INCOMING' ? 'From:' : 'To:'}
        </span>
        <span className="text-sm text-gray-600 ml-2">{message.phoneNumber}</span>
      </div>

      {/* Message Content */}
      <div className="mb-3">
        <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
          {message.content}
        </p>
      </div>

      {/* Lead Info */}
      {message.lead && (
        <div className="flex items-center space-x-2 text-xs text-gray-500 bg-gray-100 rounded px-2 py-1 w-fit">
          <Building className="w-3 h-3" />
          <span>
            {message.lead.name} ({message.lead.company}) - {message.lead.stage}
          </span>
        </div>
      )}

      {/* User Info */}
      {message.user && (
        <div className="flex items-center space-x-2 text-xs text-gray-500 mt-2">
          <User className="w-3 h-3" />
          <span>
            Sent by: {message.user.name}
          </span>
        </div>
      )}
    </div>
  );
};
